import { useState, useEffect } from "react";
import { Gift, Share2, Ticket, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useFb } from "../../utils/football";
import { hapticLight } from "../../utils/haptics";
import { toast } from "sonner";

/**
 * FootballReferralPanel — football profile referral block:
 *  1) Status card: weekly usage (X of 3), football pts from referrals, all-time
 *     successful referrals, share button (code + WhatsApp/native share).
 *  2) "Have a Referral Code?" post-signup redemption (POST /football/referral/apply).
 */
export default function FootballReferralPanel() {
  const fb = useFb();
  const { dbUser, refreshDbUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try { setStatus(await fb.get("/football/referral/status")); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!dbUser) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser]);

  const myCode = status?.referral_code || dbUser?.referral_code || "APL-XXXXXX";

  const share = async () => {
    hapticLight();
    const origin = window.location.origin;
    const text = `Join me on APL Fantasy Sports for Football World Cup 2026! ⚽\n\nUse my code: ${myCode}\n${origin}/?ref=${myCode}`;
    try {
      if (navigator.share) await navigator.share({ title: "APL Fantasy Sports", text });
      else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    } catch { /* cancelled */ }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(myCode); toast.success("Referral code copied!"); } catch { /* ignore */ }
  };

  const applyCode = async () => {
    const clean = code.trim().toUpperCase();
    if (!clean) { setError("Enter a referral code"); return; }
    setApplying(true);
    setError("");
    try {
      await fb.post("/football/referral/apply", { code: clean });
      setApplied(true);
      toast.success("Code applied! Bonus points added 🎉");
      await load();
      if (refreshDbUser) refreshDbUser();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not apply this code");
    } finally {
      setApplying(false);
    }
  };

  const hasApplied = applied || status?.has_applied_code;

  return (
    <div className="mb-6 space-y-3" data-testid="football-referral-panel">
      {/* Status card */}
      <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-900/20 to-orange-900/10 p-4" data-testid="football-referral-status">
        <div className="mb-2 flex items-center gap-2">
          <Gift className="h-5 w-5 text-amber-400" />
          <h3 className="text-sm font-bold text-amber-400">Refer &amp; Earn</h3>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-slate-900/50 p-2.5" data-testid="fb-ref-week">
            <div className="text-lg font-extrabold text-amber-400">{status?.week_used ?? 0}<span className="text-xs font-normal text-slate-500">/{status?.week_cap ?? 3}</span></div>
            <div className="text-[10px] text-slate-400">This week</div>
          </div>
          <div className="rounded-lg bg-slate-900/50 p-2.5" data-testid="fb-ref-points">
            <div className="text-lg font-extrabold text-amber-400">{status?.football_points_from_referrals ?? 0}</div>
            <div className="text-[10px] text-slate-400">Football pts</div>
          </div>
          <div className="rounded-lg bg-slate-900/50 p-2.5" data-testid="fb-ref-total">
            <div className="text-lg font-extrabold text-amber-400">{status?.total_referrals ?? 0}</div>
            <div className="text-[10px] text-slate-400">All-time</div>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Both of you earn 10 football pts + 25 cricket pts per friend. Max 3/week.</p>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={copyCode} className="flex-1 truncate rounded-lg border border-border bg-card px-3 py-2 text-left text-sm font-extrabold tracking-wide" data-testid="fb-ref-code">
            {myCode}
          </button>
          <button onClick={share} className="flex items-center gap-1.5 rounded-lg wc-bg-gold px-4 py-2 text-sm font-extrabold text-slate-900" data-testid="fb-ref-share">
            <Share2 size={15} /> Share
          </button>
        </div>
      </div>

      {/* Have a referral code? */}
      {hasApplied ? (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-300" data-testid="fb-ref-applied">
          <CheckCircle2 size={16} /> Code applied! Your signup bonus is in.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="fb-ref-redeem">
          <div className="mb-1 flex items-center gap-2">
            <Ticket size={16} className="text-amber-400" />
            <h3 className="text-sm font-bold">Have a Referral Code?</h3>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">Enter a friend's code to earn signup bonus.</p>
          <div className="flex items-center gap-2">
            <input
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase().slice(0, 12)); setError(""); }}
              maxLength={12}
              placeholder="FRIEND CODE"
              className="flex-1 rounded-lg border border-border bg-slate-900 px-3 py-2 text-sm font-bold uppercase tracking-wide text-white outline-none focus:border-amber-500"
              data-testid="fb-ref-input"
            />
            <button
              onClick={applyCode}
              disabled={applying || !code.trim()}
              className="rounded-lg wc-bg-gold px-5 py-2 text-sm font-extrabold text-slate-900 disabled:opacity-40"
              data-testid="fb-ref-apply"
            >
              {applying ? "…" : "Apply"}
            </button>
          </div>
          {error && <p className="mt-2 text-xs font-semibold text-red-400" data-testid="fb-ref-error">{error}</p>}
        </div>
      )}
    </div>
  );
}
